// Explicit, typed schema for the solver_config fields we know how to edit safely.
//
// ASSESSMENT.md P2: "Object.entries(solverConfig) สร้าง number input ทุก key" meant nested
// objects like objective_weights / cleaning_matrix / fixed_pot_intervals either failed to render
// or got silently coerced with Number(value), corrupting them. This schema lists only the flat,
// numeric fields we render as plain inputs; anything else in the stored configuration (nested
// objects, runtime-derived fields, keys we don't yet know about) is kept untouched and merged
// back in on save instead of being dropped or mis-typed.

export const SOLVER_NUMERIC_FIELDS = [
  { key: 'num_pots', label: 'Number of pots', unit: 'pots' },
  { key: 'horizon_min', label: 'Planning horizon', unit: 'min' },
  { key: 'setup_small_min', label: 'Setup (small)', unit: 'min' },
  { key: 'setup_big_min', label: 'Setup (big / allergen change)', unit: 'min' },
  { key: 'bin_capacity_kg', label: 'Bin capacity', unit: 'kg' },
  { key: 'transport_bin_min', label: 'Transport per bin', unit: 'min' },
  { key: 'room_capacity_kg', label: 'Cold-room capacity', unit: 'kg' },
  { key: 'safety_buffer_hours', label: 'Safety buffer', unit: 'hr' },
  { key: 'time_step_min', label: 'Solver time step', unit: 'min' },
  { key: 'solve_time_limit_s', label: 'Solve time limit', unit: 's' },
];

export const DEFAULT_SOLVER_CONFIG = {
  num_pots: 5,
  horizon_min: 1440,
  setup_small_min: 20,
  setup_big_min: 40,
  bin_capacity_kg: 200,
  transport_bin_min: 5,
  room_capacity_kg: 19200,
  safety_buffer_hours: 1,
  time_step_min: 5,
  solve_time_limit_s: 60,
};

const KNOWN_KEYS = new Set(SOLVER_NUMERIC_FIELDS.map((field) => field.key));

// Splits a raw solver_config object into { known, unknown } so the editable form only ever
// touches the fields it understands, while `unknown` (objective_weights, cleaning_matrix,
// fixed_pot_intervals, shift_windows if nested here, and any future keys) is preserved verbatim.
export function splitSolverConfig(solverConfig) {
  const source = solverConfig && typeof solverConfig === 'object' ? solverConfig : {};
  const known = {};
  const unknown = {};
  Object.entries(source).forEach(([key, value]) => {
    if (KNOWN_KEYS.has(key) && (typeof value === 'number' || value === '' || value == null)) {
      known[key] = value;
    } else if (KNOWN_KEYS.has(key)) {
      // Known key but not a plain number (unexpected shape) — do not attempt to coerce it.
      unknown[key] = value;
    } else {
      unknown[key] = value;
    }
  });
  return {
    known: { ...DEFAULT_SOLVER_CONFIG, ...known },
    unknown,
  };
}

export function mergeSolverConfig(known, unknown) {
  return { ...unknown, ...known };
}
